import React, { createContext, useCallback, useMemo } from "react";
import { useRouter } from "next/router";

import {
  useEnterpriseData,
  UseEnterpriseDataReturnType,
} from "@/hooks/useEnterpriseData";
import {
  Enterprise,
  EnterpriseAddonType,
  EnterpriseStates,
  EnterpriseSubmoduleType,
  FeatureToggles,
} from "@/types/dataTypes";

export type EnterpriseContextType = {
  // Core data - full access to enterprise object
  enterprise: Enterprise | null;
  enterpriseId: string | null;

  // Query state
  isLoading: boolean;
  isError: boolean;
  error: unknown;

  // Convenience computed values
  /**
   * Whether the enterprise is a demo enterprise.
   * Based on enterprise.states.isDemoEnterprise database field.
   */
  isDemo: boolean;

  /**
   * Whether enterprise has a PRO subscription.
   * Read directly from the enterprise.subscription database column.
   */
  isPro: boolean;

  // Convenience helper functions
  /**
   * Check if enterprise has a specific addon.
   *
   * @param addonType - The addon type to check (e.g., 'NATURAL_CAPITAL')
   * @returns true if the addon is subscribed, false otherwise
   *
   * @example
   * const hasNaturalCapital = hasAddon('NATURAL_CAPITAL');
   */
  hasAddon: (addonType: string) => boolean;

  /**
   * Check if enterprise has a specific submodule.
   *
   * @param type - The submodule type to check
   * @returns true if the submodule is available, false otherwise
   */
  hasSubmodule: (type: string) => boolean;

  // Convenience direct access to commonly used nested objects
  featureToggles: FeatureToggles | undefined;
  states: EnterpriseStates | undefined;

  /**
   * Raw array of subscribed addons.
   * Use this for explicit addon checking or custom logic.
   *
   * @example
   * const hasNC = subscribedAddons.some(a => a.type === 'NATURAL_CAPITAL');
   */
  subscribedAddons: EnterpriseAddonType[];

  /**
   * Raw array of available submodules.
   */
  availableSubmodules: EnterpriseSubmoduleType[];
};

export const EnterpriseContext =
  createContext<EnterpriseContextType | undefined>(undefined);

type EnterpriseProviderProps = {
  children: React.ReactNode;
};

export const EnterpriseProvider: React.FC<EnterpriseProviderProps> = ({
  children,
}) => {
  const router = useRouter();
  const enterpriseIdRaw = router.query.enterpriseId;
  const enterpriseId = Array.isArray(enterpriseIdRaw)
    ? enterpriseIdRaw[0]
    : enterpriseIdRaw;

  // Fetch enterprise data once at this level
  const {
    enterpriseQueryResult,
    subscribedAddons,
    availableSubmodules,
  }: UseEnterpriseDataReturnType = useEnterpriseData(enterpriseId ?? "", {
    // Only fetch when we actually have an enterpriseId
    fetchEnterpriseData:
      typeof enterpriseId === "string" && enterpriseId.length > 0,
  });

  const enterprise = enterpriseQueryResult.data ?? null;

  // Computed values
  const isDemo = enterprise?.states?.isDemoEnterprise === true;
  const isPro = enterprise?.subscription === "PRO";

  // Helper function: Check if enterprise has a specific addon
  const hasAddon = useCallback(
    (addonType: string): boolean => {
      if (!Array.isArray(subscribedAddons)) return false;
      return subscribedAddons.some((addon) => addon?.type === addonType);
    },
    [subscribedAddons]
  );

  // Helper function: Check if enterprise has a specific submodule
  const hasSubmodule = useCallback(
    (type: string): boolean => {
      if (!Array.isArray(availableSubmodules)) return false;
      return availableSubmodules.some((m) => m?.type === type);
    },
    [availableSubmodules]
  );

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      enterprise,
      enterpriseId: enterpriseId ?? null,
      isLoading: enterpriseQueryResult.isLoading,
      isError: enterpriseQueryResult.isError,
      error: enterpriseQueryResult.error,
      isDemo,
      isPro,
      hasAddon,
      hasSubmodule,
      featureToggles: enterprise?.featureToggles,
      states: enterprise?.states,
      subscribedAddons: subscribedAddons || [],
      availableSubmodules: availableSubmodules || [],
    }),
    [
      enterprise,
      enterpriseId,
      enterpriseQueryResult.isLoading,
      enterpriseQueryResult.isError,
      enterpriseQueryResult.error,
      isDemo,
      isPro,
      hasAddon,
      hasSubmodule,
      subscribedAddons,
      availableSubmodules,
    ]
  );

  return (
    <EnterpriseContext.Provider value={value}>
      {children}
    </EnterpriseContext.Provider>
  );
};
